/**
 * Campaign view-model for the "Find my pack" chooser (step 2).
 *
 * The chooser lists the available CAMPAIGNS (the session's bound campaign + the
 * recommended ones) plus a synthesized "Custom" entry — mirroring the canonical
 * Spark configurator's engagement step (`useEngagementStep`), not the catalog
 * bundles. We reuse the app's own campaign price/Custom helpers so the math is
 * identical to the rest of the app.
 */

import {
  buildCustomCampaign,
  computeCampaignOffer,
  getGamificationItems,
  CONFIGURATOR_ENGAGEMENT_CUSTOM_CAMPAIGN_ID,
} from '@/components/modules/spark/configurator/utils';
import {
  type Campaign,
  type DefaultItem,
  type TextTranslations,
} from '@/components/modules/spark/configurator/types';

/** A campaign rendered as a selectable pack in the chooser. */
export interface CampaignOption {
  id: string;
  name: string;
  /** The free/included baseline campaign. */
  isBasic: boolean;
  /** The synthesized "Custom" entry reflecting the user's current selection. */
  isCustom: boolean;
  /**
   * Stable tier rank among the real packs (cheapest = 0), used to pick the pack's
   * colour/gem style. Derived from PRICE, never the array position — `session.campaigns`
   * is reordered when a pack is applied (the chosen pack is hoisted to the front), so a
   * positional index would collapse every selected pack to style 0. 0 for Basic/Custom.
   */
  styleIndex: number;
  /** Heuristic "Our recommendation" flag (no backend label exists yet). */
  recommended: boolean;
  /** Display price (discounted if any, else base); 0 for the Basic baseline. */
  price: number;
  /** Original (pre-discount) price, when discounted. */
  originalPrice?: number;
  /** Saved percentage (0 when none). */
  savedPct: number;
  /** Gamification item-ids active (non-Off) in this pack — for the "includes" row. */
  featureIds: number[];
  /** Underlying campaign (used to apply / sync feature tiers). */
  campaign: Campaign;
}

/**
 * Campaign names can arrive as a localized map even though the type says `string`.
 * Picks `lang` (defaulting to English, skipping empty placeholders), else English,
 * else the first non-empty value.
 */
export function campaignName(
  name: Campaign['name'] | TextTranslations | null | undefined,
  lang = 'en',
): string {
  if (typeof name === 'string') return name;
  if (name && typeof name === 'object') {
    const map = name as TextTranslations;
    const nonEmpty = (v: unknown): string | undefined =>
      typeof v === 'string' && v.trim() !== '' ? v : undefined;
    return nonEmpty(map[lang]) ?? nonEmpty(map.en) ?? Object.values(map).map(nonEmpty).find(Boolean) ?? '';
  }
  return '';
}

/**
 * The pack's DISPLAY name from its ART BUNDLE (`art_bundle[].properties.display_name`, a localized
 * map), while the campaign `name` is an internal id (e.g. "MX low-pricing … EDGE"). Prefer the
 * art-bundle display name; fall back to `text_name` / `properties.name`, then the raw `name` when
 * the bundle is absent/empty. Pass NO `lang` (English) for IDENTITY checks (isBasic / pack-name
 * match) — only the shown name should be localized.
 */
export function campaignDisplayName(campaign: Campaign, lang = 'en'): string {
  for (const bundle of campaign.art_bundle ?? []) {
    const name =
      campaignName(bundle?.properties?.display_name, lang) ||
      campaignName(bundle?.text_name, lang) ||
      campaignName(bundle?.properties?.name, lang);
    if (name) return name;
  }
  return campaignName(campaign.name, lang);
}

/**
 * Merge the session's bound campaign(s) with the recommended ones, deduped by id. When the same
 * campaign is in both, prefer the PROPERLY PRICED copy (`price_evaluation.match === true`): the
 * bound campaign from `/load` carries an unpriced placeholder (`match: false`, `featuresPrice: 0`)
 * until a pack is actually applied, and it must NOT shadow the matched, real-priced version from
 * `recommendedCampaigns` — otherwise e.g. Edge reads "$0 / Included".
 */
export function mergeCampaigns(campaigns: Campaign[] = [], recommended: Campaign[] = []): Campaign[] {
  const priced = (c: Campaign): boolean => c.price_evaluation?.match === true;
  const byId = new Map<string, Campaign>();
  // Recommended first (the matched, priced source of truth), then the session's bound campaigns; a
  // bound copy only replaces a recommended one when the recommended is unpriced and the bound isn't.
  for (const c of [...recommended, ...campaigns]) {
    const id = String(c.id);
    const existing = byId.get(id);
    if (!existing || (!priced(existing) && priced(c))) byId.set(id, c);
  }
  return [...byId.values()];
}

/** Per-tier price + enablement resolved from a campaign's item ladders. */
export interface CampaignTierPrice {
  /** List (pre-discount) price for this item-tier, in the campaign's market currency. */
  base: number;
  /** Discounted price when the campaign discounts this tier. */
  discounted?: number;
  /** False when the campaign disables this tier (not selectable). */
  enabled: boolean;
}

/**
 * Build a `${itemId}:${tierId}` → price/enablement map from a campaign's
 * `items[].tiers[]`. This is the authoritative per-tier price source the Spark
 * configurator uses in its engagement step (`useEngagementStep`) — NOT the catalog's
 * per-item `cost`, which is USD-only and campaign-agnostic. A tier absent from the map
 * is simply not priced by this campaign.
 */
export function campaignTierPriceMap(campaign: Campaign | undefined): Map<string, CampaignTierPrice> {
  const map = new Map<string, CampaignTierPrice>();
  for (const item of campaign?.items ?? []) {
    for (const tier of item.tiers ?? []) {
      map.set(`${item.id}:${tier.id}`, {
        base: tier.base_price ?? 0,
        discounted: tier.discounted_price ?? undefined,
        enabled: tier.enabled !== false,
      });
    }
  }
  return map;
}

/**
 * The CATALOG price map = the UNION of every campaign's tier ladders, keyed
 * `${itemId}:${tierId}`. The backend ships no standalone "Basic" campaign, but every
 * commercial pack carries the SAME full ladder of features at identical base prices — so
 * the catalog is the merge of all of them: the MAX base price (so a pack that zeroes a tier
 * it disables can't hide its real price) and `enabled` if ANY pack enables the tier. Base
 * prices only — pack discounts fold into the bundle total, never the per-feature catalog price.
 */
export function catalogTierPriceMap(campaigns: Campaign[]): Map<string, CampaignTierPrice> {
  const map = new Map<string, CampaignTierPrice>();
  for (const campaign of campaigns) {
    for (const item of campaign.items ?? []) {
      for (const tier of item.tiers ?? []) {
        const key = `${item.id}:${tier.id}`;
        const base = tier.base_price ?? 0;
        const enabled = tier.enabled !== false;
        const prev = map.get(key);
        map.set(key, {
          base: prev ? Math.max(prev.base, base) : base,
          discounted: undefined,
          enabled: prev ? prev.enabled || enabled : enabled,
        });
      }
    }
  }
  return map;
}

/** Gamification `default_items` → `{ [itemId]: tierId }` (skips non-numeric tiers). */
export function campaignFeatureTiers(campaign: Campaign): Record<number, number> {
  const tiers: Record<number, number> = {};
  for (const item of getGamificationItems(campaign.default_items ?? [])) {
    const tierId = Number(item.tier_id);
    if (!Number.isNaN(tierId)) tiers[item.item_id] = tierId;
  }
  return tiers;
}

/** True when the session items deviate from the campaign's default gamification items. */
export function isCustomSelection(campaign: Campaign | undefined, sessionItems: DefaultItem[] = []): boolean {
  if (!campaign?.default_items?.length || !sessionItems.length) return false;
  const defaults = getGamificationItems(campaign.default_items);
  const selected = getGamificationItems(sessionItems);
  return defaults.some((d) => {
    const match = selected.find((s) => s.item_id === d.item_id);
    return !match || String(match.tier_id) !== String(d.tier_id);
  });
}

/** Item-ids whose active campaign tier is NOT an "Off" tier. */
function activeFeatureIds(campaign: Campaign): number[] {
  const ids: number[] = [];
  for (const d of getGamificationItems(campaign.default_items ?? [])) {
    // Match on the type too: ids are only unique per type (the Setup BASE_ITEM and the Loyalty & Shop
    // GAMIFICATION_ITEM are both id 1), so an id-only lookup resolved Loyalty & Shop to Setup and
    // dropped it from the pack.
    const item = campaign.items?.find((it) => Number(it.id) === d.item_id && it.type === d.type);
    const tier = item?.tiers?.find((t) => String(t.id) === String(d.tier_id));
    // A DISABLED FEATURE (`item.enabled === false`) is not part of the pack, so don't list it as
    // "included" (matches the pack-price rule + the public `/pricing` API). A disabled TIER on an
    // enabled feature IS included (see the note in `campaignFeatureBreakdown`).
    if (item?.enabled === false) continue;
    if (tier && !tier.name?.toLowerCase().includes('off')) ids.push(d.item_id);
  }
  return ids;
}

/**
 * Features-only price of a pack = the backend's DIFFERENTIATED `price_evaluation.featuresPrice`
 * (the discounted variant when `match`), authoritative — no FE summing/subtracting. Setup is a
 * separate figure (step 4). Falls back to summing the gamification `default_items` (excluding
 * disabled FEATURES) for data that doesn't carry the breakdown. `features` is the discounted
 * figure; `featuresOriginal` the pre-discount one.
 */
function campaignFeatureBreakdown(campaign: Campaign): { features: number; featuresOriginal: number } {
  const pe = campaign.price_evaluation;
  if (pe?.featuresPrice != null) {
    const featuresOriginal = pe.featuresPrice;
    // `match` gates the discount: false → no discount computed, use the base features price.
    const features = pe.match ? pe.discountedFeaturesPrice ?? featuresOriginal : featuresOriginal;
    return { features, featuresOriginal };
  }
  // Fallback (older data / synthesized Basic without the breakdown): sum the default items,
  // excluding only a DISABLED FEATURE (`item.enabled === false`) — the public /pricing item-only rule.
  let features = 0;
  let featuresOriginal = 0;
  for (const d of getGamificationItems(campaign.default_items ?? [])) {
    // Type-matched like `activeFeatureIds` (ids are only unique per type).
    const item = campaign.items?.find((it) => Number(it.id) === d.item_id && it.type === d.type);
    const tier = item?.tiers?.find((t) => String(t.id) === String(d.tier_id));
    if (!tier) continue;
    if (item?.enabled === false) continue;
    const base = tier.base_price ?? 0;
    features += tier.discounted_price ?? base;
    featuresOriginal += base;
  }
  return { features, featuresOriginal };
}

function toOption(campaign: Campaign, recommended: boolean, lang = 'en'): CampaignOption {
  const name = campaignDisplayName(campaign, lang);
  // Pack price = features only (no setup). A $0 pack (the API's "Basic" baseline) renders "Included"
  // and takes the baseline styling.
  const { features, featuresOriginal } = campaignFeatureBreakdown(campaign);
  // "Basic" = the $0 pack: its FEATURES cost nothing (every feature Off, or bundled free like
  // Meta-Jackpot Plus — so an "all features Off" test is WRONG, Basic isn't all-Off). This is
  // reliable only because `mergeCampaigns` now prefers the matched/priced copy over the session's
  // unpriced bound placeholder (`price_evaluation.match=false → features 0`), which previously made
  // a fully-featured pack (e.g. Edge) read $0 and masquerade as Basic (the "two Basic cards" bug).
  const basic = features === 0;
  const originalPrice = !basic && featuresOriginal > features ? featuresOriginal : undefined;
  const savedPct = originalPrice && originalPrice > 0 ? Math.round((1 - features / originalPrice) * 100) : 0;
  return {
    id: String(campaign.id),
    name,
    isBasic: basic,
    isCustom: false,
    styleIndex: 0, // real packs get their stable rank in assignStyleIndex(); Basic stays 0.
    recommended,
    price: features,
    originalPrice,
    savedPct,
    // Always list the pack's active (non-Off) features — Basic included: it costs $0 but still
    // bundles feature(s) for free (e.g. Meta-Jackpot Plus), so an empty "includes" row is wrong.
    // A truly all-Off pack yields [] here naturally, so no special-casing is needed.
    featureIds: activeFeatureIds(campaign),
    campaign,
  };
}

/**
 * Stamp each real (non-Basic, non-Custom) pack with its stable tier rank by ascending
 * price (cheapest = 0), so its colour/gem style is tied to the pack itself and not to a
 * position in `campaigns` (which `sessionSetRecommendedCampaign` reorders, hoisting the
 * chosen pack to the front — that's what made every selected pack render style 0). Ties
 * break on id for determinism. Basic/Custom keep styleIndex 0 (they use their own styles).
 */
function assignStyleIndex(options: CampaignOption[]): CampaignOption[] {
  const ranked = options
    .filter((o) => !o.isBasic && !o.isCustom)
    .sort((a, b) => a.price - b.price || a.id.localeCompare(b.id));
  const rankById = new Map(ranked.map((o, i) => [o.id, i]));
  return options.map((o) => (rankById.has(o.id) ? { ...o, styleIndex: rankById.get(o.id) ?? 0 } : o));
}

/**
 * Build the chooser's pack list: EXACTLY the campaigns the recommended-campaigns API returns,
 * ordered by price, plus a synthesized "Custom" entry reflecting the user's current selection.
 * No name-based Basic detection and no synthesized Basic — the API ships Basic as a real $0
 * campaign. The Custom entry only appears once the user has gamification picks.
 */
export function buildCampaignOptions(
  merged: Campaign[],
  activeCampaign: Campaign | undefined,
  isCustomPack: boolean,
  sessionItems: DefaultItem[],
  currency: string,
  lang = 'en',
): CampaignOption[] {
  // Ordered by ASCENDING PRICE (cheapest first, ties broken on id) — the STABLE display order.
  // `sessionSetRecommendedCampaign` reorders `session.campaigns` (the chosen pack is hoisted to the
  // front), so the raw merge order would reshuffle the cards on every apply; price is intrinsic to
  // each pack, so it pins every card to a fixed slot. Basic is the $0 pack, so it sorts first.
  const options = merged
    .map((c) => toOption(c, false, lang))
    .sort((a, b) => a.price - b.price || a.id.localeCompare(b.id));
  // Recommendation heuristic: the middle-priced PAID pack (no backend label exists yet). Excludes
  // the $0 Basic so the badge always lands on a purchasable pack (e.g. Edge, the middle of three).
  const paid = options.filter((o) => !o.isBasic);
  const reco = paid.length ? paid[Math.floor((paid.length - 1) / 2)] : undefined;
  const ranked = reco ? options.map((o) => (o.id === reco.id ? { ...o, recommended: true } : o)) : options;

  const customOffer = computeCampaignOffer(activeCampaign, isCustomPack, currency);
  const customCampaign = buildCustomCampaign(activeCampaign, getGamificationItems(sessionItems), customOffer);
  if (!customCampaign) return assignStyleIndex(ranked);

  const offer = computeCampaignOffer(customCampaign, true, currency);
  const savedPct =
    offer.originalPrice && offer.originalPrice > 0 ? Math.round((1 - offer.price / offer.originalPrice) * 100) : 0;
  const customOption: CampaignOption = {
    id: CONFIGURATOR_ENGAGEMENT_CUSTOM_CAMPAIGN_ID,
    name: 'Custom',
    isBasic: false,
    isCustom: true,
    styleIndex: 0, // Custom uses its own green style (custom flag), not a PACK_STYLES rank.
    recommended: false,
    price: offer.price,
    originalPrice: offer.originalPrice,
    savedPct,
    featureIds: activeFeatureIds(customCampaign),
    campaign: customCampaign,
  };
  return assignStyleIndex([...ranked, customOption]);
}
